import React from 'react';
import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { formatCurrency, getCurrencySymbol } from '@src/scripts/util/currency';

// Reference tool (simuprint.vercel.app) "Optional layers" panel — inline
// line icons, matching ResultsStep.js's convention of raw <svg> primitives
// (copied 1:1 from the reference tool's own icon paths) rather than an icon
// library.
const ChairIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 11h16" />
		<path d="M6 11V7a3 3 0 0 1 6 0v4" />
		<path d="M5 11l-1 7" />
		<path d="M19 11l1 7" />
		<path d="M8 18h8" />
	</svg>
);

const FinancingIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M3 12h12" />
		<path d="M11 8l4 4-4 4" />
		<path d="M21 4v16" />
	</svg>
);

const CameraIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
		<circle cx="12" cy="13" r="4" />
	</svg>
);

const CheckIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 12l5 5L20 6" />
	</svg>
);

const ChevronIcon = () => (
	<svg
		className="roi-calculator__layer-disclosure-chevron"
		width="11"
		height="11"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M9 18l6-6-6-6" />
	</svg>
);

// Same "clamp to >= 0" numeric input as IndicationsTable.js's numericInput
// helper — kept local (rather than imported) since that one isn't exported
// and this file's fields have their own suffix/label wrapper shape.
const layerNumberInput = ( {
	id,
	label,
	value,
	suffix,
	step = '1',
	onChange,
} ) => (
	<div className="roi-calculator__layer-input">
		<input
			id={ id }
			className="roi-calculator__layer-input-control"
			aria-label={ label }
			type="number"
			min="0"
			step={ step }
			value={ value ?? '' }
			onChange={ ( event ) => {
				const raw = event.target.value;

				if ( raw === '' ) {
					onChange( null );
					return;
				}

				const parsed = Number( raw );
				onChange(
					Number.isFinite( parsed ) ? Math.max( 0, parsed ) : 0
				);
			} }
		/>
		{ suffix && (
			<span className="roi-calculator__layer-input-suffix">
				{ suffix }
			</span>
		) }
	</div>
);

// `id` associates the label with layerNumberInput's <input id={id}> above —
// the read-only "Monthly payment (auto)" field has no control to associate
// with, so it renders a plain <div> there instead of an orphaned <label>.
const layerField = ( { id, label, hint, children } ) =>
	id ? (
		<div className="roi-calculator__layer-field">
			<label htmlFor={ id } className="roi-calculator__layer-field-label">
				{ label }
			</label>
			<p className="roi-calculator__layer-field-hint">{ hint }</p>
			{ children }
		</div>
	) : (
		<div className="roi-calculator__layer-field">
			<div className="roi-calculator__layer-field-label">{ label }</div>
			<p className="roi-calculator__layer-field-hint">{ hint }</p>
			{ children }
		</div>
	);

// The switch control every module card uses to enable/disable itself.
// Distinct from IndicationsTable.js's `&__toggle` (a checkmark button for
// per-row enable/disable) — the reference tool's optional layers use a
// sliding on/off switch instead, which nothing else in this calculator
// currently needs.
const LayerToggle = ( { enabled, label, onToggle } ) => (
	<button
		type="button"
		className={
			'roi-calculator__layer-switch' + ( enabled ? ' is-on' : '' )
		}
		role="switch"
		aria-checked={ enabled }
		aria-label={ label }
		onClick={ onToggle }
	>
		<span className="roi-calculator__layer-switch-knob" />
	</button>
);

const layerResultCell = ( label, value ) => (
	<div className="roi-calculator__layer-cell">
		<div className="roi-calculator__layer-cell-label">{ label }</div>
		<div className="roi-calculator__layer-cell-value">{ value }</div>
	</div>
);

const chairTimeFreedCard = ( {
	title,
	description,
	enabled,
	onToggle,
	freedSlotsPerMonth,
	replacementActFee,
	onChangeReplacementActFee,
	targetConversionRate,
	onChangeTargetConversionRate,
	provisionalCasesAvoided,
	onChangeProvisionalCasesAvoided,
	savingPerProvisional,
	onChangeSavingPerProvisional,
	chairTimeMonthly,
	annualPotential,
	currency,
	locale,
} ) => {
	const currencySymbol = getCurrencySymbol( currency, locale );

	return (
		<div className="roi-calculator__layer-card" key="chair-time-freed">
			<div className="roi-calculator__layer-head">
				<div className="roi-calculator__layer-head-main">
					<div className="roi-calculator__layer-icon">
						<ChairIcon />
					</div>
					<div>
						<div className="roi-calculator__layer-title-row">
							<h4>
								{ title ??
									__(
										'Chair time freed',
										'sprintray-roi-calculator'
									) }
							</h4>
							<span className="roi-calculator__layer-badge">
								{ __(
									'Optional module',
									'sprintray-roi-calculator'
								) }
							</span>
						</div>
						<p className="roi-calculator__layer-description">
							{ description ??
								__(
									'Crown in 1 appointment (Midas) → end of the 2nd visit, provisional and lab wait. Enable this layer to value the time saved as additional potential.',
									'sprintray-roi-calculator'
								) }
						</p>
					</div>
				</div>
				<LayerToggle
					enabled={ enabled }
					label={ __(
						'Enable chair time freed layer',
						'sprintray-roi-calculator'
					) }
					onToggle={ onToggle }
				/>
			</div>

			{ enabled && (
				<div className="roi-calculator__layer-body">
					<div className="roi-calculator__layer-stat">
						<div
							className="roi-calculator__layer-stat-icon"
							aria-hidden="true"
						>
							🪑
						</div>
						<div>
							<div className="roi-calculator__layer-stat-value">
								<span>{ freedSlotsPerMonth }</span>
								<span>
									{ __(
										'freed slot(s) / month',
										'sprintray-roi-calculator'
									) }
								</span>
							</div>
							<div className="roi-calculator__layer-stat-sub">
								{ sprintf(
									/* translators: %d: number of Midas cases per month */
									__(
										'computed from your %d Midas cases / month',
										'sprintray-roi-calculator'
									),
									freedSlotsPerMonth
								) }
							</div>
						</div>
					</div>

					<div className="roi-calculator__layer-fields">
						{ layerField( {
							id: 'roi-calculator-layer-replacement-fee',
							label: __(
								'Replacement act fee',
								'sprintray-roi-calculator'
							),
							hint: __(
								'Average revenue per new Midas patient',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-replacement-fee',
								label: __(
									'Replacement act fee',
									'sprintray-roi-calculator'
								),
								value: replacementActFee,
								suffix: currencySymbol,
								onChange: onChangeReplacementActFee,
							} ),
						} ) }
						{ layerField( {
							id: 'roi-calculator-layer-conversion-rate',
							label: __(
								'Target conversion rate',
								'sprintray-roi-calculator'
							),
							hint: __(
								'Share of freed slots converted to new acts',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-conversion-rate',
								label: __(
									'Target conversion rate',
									'sprintray-roi-calculator'
								),
								value: targetConversionRate,
								suffix: '%',
								onChange: onChangeTargetConversionRate,
							} ),
						} ) }
					</div>

					<div className="roi-calculator__layer-formula">
						<span className="roi-calculator__layer-formula-value">
							{ freedSlotsPerMonth }
						</span>
						<span>×</span>
						<span className="roi-calculator__layer-formula-value">
							{ `${ targetConversionRate ?? 0 } %` }
						</span>
						<span>×</span>
						<span className="roi-calculator__layer-formula-value">
							{ formatCurrency(
								replacementActFee ?? 0,
								currency,
								locale
							) }
						</span>
						<span>=</span>
						<span className="roi-calculator__layer-formula-result">
							{ formatCurrency(
								chairTimeMonthly,
								currency,
								locale
							) }
						</span>
						<span>
							/ { __( 'month', 'sprintray-roi-calculator' ) }
						</span>
					</div>

					<details className="roi-calculator__layer-disclosure" open>
						<summary>
							<ChevronIcon />
							{ __(
								'Avoided provisionals (optional)',
								'sprintray-roi-calculator'
							) }
						</summary>
						<div className="roi-calculator__layer-fields">
							{ layerField( {
								id: 'roi-calculator-layer-provisional-cases',
								label: __(
									'Provisional cases avoided / month',
									'sprintray-roi-calculator'
								),
								hint: __(
									'Cost + time of suppressed provisional',
									'sprintray-roi-calculator'
								),
								children: layerNumberInput( {
									id: 'roi-calculator-layer-provisional-cases',
									label: __(
										'Provisional cases avoided / month',
										'sprintray-roi-calculator'
									),
									value: provisionalCasesAvoided,
									suffix: __(
										'cases',
										'sprintray-roi-calculator'
									),
									onChange: onChangeProvisionalCasesAvoided,
								} ),
							} ) }
							{ layerField( {
								id: 'roi-calculator-layer-provisional-saving',
								label: __(
									'Saving per provisional',
									'sprintray-roi-calculator'
								),
								hint: __(
									'Material + assistant time',
									'sprintray-roi-calculator'
								),
								children: layerNumberInput( {
									id: 'roi-calculator-layer-provisional-saving',
									label: __(
										'Saving per provisional',
										'sprintray-roi-calculator'
									),
									value: savingPerProvisional,
									suffix: currencySymbol,
									onChange: onChangeSavingPerProvisional,
								} ),
							} ) }
						</div>
					</details>

					<div className="roi-calculator__layer-result">
						<div>
							<div className="roi-calculator__layer-result-label">
								{ __(
									'Additional potential',
									'sprintray-roi-calculator'
								) }
							</div>
							<div className="roi-calculator__layer-result-hint">
								{ __(
									'Chair time + avoided provisionals. Not included in the main ROI figure.',
									'sprintray-roi-calculator'
								) }
							</div>
						</div>
						<div className="roi-calculator__layer-result-amount">
							<div>
								{ formatCurrency(
									annualPotential,
									currency,
									locale
								) }
							</div>
							<div className="roi-calculator__layer-result-unit">
								{ __( 'per year', 'sprintray-roi-calculator' ) }
							</div>
						</div>
					</div>
				</div>
			) }
		</div>
	);
};

const financingCard = ( {
	title,
	description,
	enabled,
	onToggle,
	leaseTermMonths,
	onChangeLeaseTermMonths,
	annualInterestRate,
	onChangeAnnualInterestRate,
	calculatedMonthlyRate,
	contractualRateOverride,
	onChangeContractualRateOverride,
	monthlySavings,
	effectiveMonthlyRate,
	netMonthlyCashFlow,
	currency,
	locale,
} ) => {
	const currencySymbol = getCurrencySymbol( currency, locale );
	const isPositive = netMonthlyCashFlow >= 0;

	return (
		<div className="roi-calculator__layer-card" key="financing">
			<div className="roi-calculator__layer-head">
				<div className="roi-calculator__layer-head-main">
					<div className="roi-calculator__layer-icon">
						<FinancingIcon />
					</div>
					<div>
						<div className="roi-calculator__layer-title-row">
							<h4>
								{ title ??
									__(
										'Financing (lease / LOA)',
										'sprintray-roi-calculator'
									) }
							</h4>
							<span className="roi-calculator__layer-badge">
								{ __(
									'Optional module',
									'sprintray-roi-calculator'
								) }
							</span>
						</div>
						<p className="roi-calculator__layer-description">
							{ description ??
								__(
									'Compare your monthly cash flow rather than the upfront price: if savings cover the monthly payment, the equipment pays for itself.',
									'sprintray-roi-calculator'
								) }
						</p>
					</div>
				</div>
				<LayerToggle
					enabled={ enabled }
					label={ __(
						'Enable financing layer',
						'sprintray-roi-calculator'
					) }
					onToggle={ onToggle }
				/>
			</div>

			{ enabled && (
				<div className="roi-calculator__layer-body">
					<div className="roi-calculator__layer-fields roi-calculator__layer-fields--four">
						{ layerField( {
							id: 'roi-calculator-layer-lease-term',
							label: __(
								'Financing duration',
								'sprintray-roi-calculator'
							),
							hint: __(
								'48 to 84 months typically',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-lease-term',
								label: __(
									'Financing duration',
									'sprintray-roi-calculator'
								),
								value: leaseTermMonths,
								suffix: __(
									'months',
									'sprintray-roi-calculator'
								),
								onChange: ( value ) =>
									onChangeLeaseTermMonths( value ?? 0 ),
							} ),
						} ) }
						{ layerField( {
							id: 'roi-calculator-layer-lease-interest',
							label: __(
								'Annual interest rate',
								'sprintray-roi-calculator'
							),
							hint: __(
								'(optional) refines the monthly payment using the PMT formula',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-lease-interest',
								label: __(
									'Annual interest rate',
									'sprintray-roi-calculator'
								),
								value: annualInterestRate || null,
								suffix: '%',
								step: '0.1',
								onChange: ( value ) =>
									onChangeAnnualInterestRate( value ?? 0 ),
							} ),
						} ) }
						{ layerField( {
							label: __(
								'Monthly payment (auto)',
								'sprintray-roi-calculator'
							),
							hint: __(
								'Calculated: price / duration, or PMT if rate entered',
								'sprintray-roi-calculator'
							),
							children: (
								<div className="roi-calculator__layer-input roi-calculator__layer-input--readonly">
									{ formatCurrency(
										calculatedMonthlyRate,
										currency,
										locale
									) }
								</div>
							),
						} ) }
						{ layerField( {
							id: 'roi-calculator-layer-lease-contractual',
							label: __(
								'Or: contractual monthly payment',
								'sprintray-roi-calculator'
							),
							hint: __(
								'Enter the exact amount from your lease offer',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-lease-contractual',
								label: __(
									'Contractual monthly payment',
									'sprintray-roi-calculator'
								),
								value: contractualRateOverride,
								suffix: currencySymbol,
								onChange: onChangeContractualRateOverride,
							} ),
						} ) }
					</div>

					<div
						className={
							'roi-calculator__layer-cashflow' +
							( isPositive ? ' is-positive' : ' is-negative' )
						}
					>
						<div className="roi-calculator__layer-cashflow-main">
							<div className="roi-calculator__layer-cashflow-label">
								{ __(
									'Net monthly cash flow',
									'sprintray-roi-calculator'
								) }
							</div>
							<div className="roi-calculator__layer-cashflow-value">
								{ `${ isPositive ? '+' : '' }${ formatCurrency(
									netMonthlyCashFlow,
									currency,
									locale
								) }` }
							</div>
							<p className="roi-calculator__layer-cashflow-note">
								{ isPositive ? (
									<>
										<strong>
											{ __(
												'The equipment pays for itself',
												'sprintray-roi-calculator'
											) }
										</strong>
										{ sprintf(
											/* translators: %s: formatted net monthly cash flow */
											__(
												': your savings cover the monthly payment from day one — with %s left as net monthly cash.',
												'sprintray-roi-calculator'
											),
											formatCurrency(
												netMonthlyCashFlow,
												currency,
												locale
											)
										) }
									</>
								) : (
									<>
										<strong>
											{ __(
												'The monthly payment exceeds your savings',
												'sprintray-roi-calculator'
											) }
										</strong>
										{ sprintf(
											/* translators: %s: formatted shortfall amount */
											__(
												": you're short %s of net monthly cash — reconsider the financing terms.",
												'sprintray-roi-calculator'
											),
											formatCurrency(
												Math.abs( netMonthlyCashFlow ),
												currency,
												locale
											)
										) }
									</>
								) }
							</p>
						</div>
						{ isPositive && (
							<div className="roi-calculator__layer-cashflow-badge">
								<CheckIcon />
							</div>
						) }
					</div>

					<div className="roi-calculator__layer-cell-grid roi-calculator__layer-cell-grid--three">
						{ layerResultCell(
							__( 'Savings / month', 'sprintray-roi-calculator' ),
							formatCurrency( monthlySavings, currency, locale )
						) }
						{ layerResultCell(
							__( 'Lease / month', 'sprintray-roi-calculator' ),
							formatCurrency(
								effectiveMonthlyRate,
								currency,
								locale
							)
						) }
						{ layerResultCell(
							__( 'Net / month', 'sprintray-roi-calculator' ),
							`${ isPositive ? '+' : '' }${ formatCurrency(
								netMonthlyCashFlow,
								currency,
								locale
							) }`
						) }
					</div>
				</div>
			) }
		</div>
	);
};

const intraoralCameraCard = ( {
	title,
	description,
	enabled,
	onToggle,
	cameraMonthlyPayment,
	onChangeCameraMonthlyPayment,
	monthsForRoi,
	adjustedRoiMonths,
	addedMonths,
	currency,
	locale,
} ) => {
	const currencySymbol = getCurrencySymbol( currency, locale );

	return (
		<div
			className="roi-calculator__layer-card roi-calculator__layer-card--wide"
			key="intraoral-camera"
		>
			<div className="roi-calculator__layer-head">
				<div className="roi-calculator__layer-head-main">
					<div className="roi-calculator__layer-icon">
						<CameraIcon />
					</div>
					<div>
						<div className="roi-calculator__layer-title-row">
							<h4>
								{ title ??
									__(
										'Intraoral camera',
										'sprintray-roi-calculator'
									) }
							</h4>
							<span className="roi-calculator__layer-badge">
								{ __(
									'Optional module',
									'sprintray-roi-calculator'
								) }
							</span>
						</div>
						<p className="roi-calculator__layer-description">
							{ description ??
								__(
									'Include the financing cost of your intraoral camera for a complete digitisation ROI — SprintRay system + camera.',
									'sprintray-roi-calculator'
								) }
						</p>
					</div>
				</div>
				<LayerToggle
					enabled={ enabled }
					label={ __(
						'Enable intraoral camera layer',
						'sprintray-roi-calculator'
					) }
					onToggle={ onToggle }
				/>
			</div>

			{ enabled && (
				<div className="roi-calculator__layer-body">
					<div className="roi-calculator__layer-fields roi-calculator__layer-fields--single">
						{ layerField( {
							id: 'roi-calculator-layer-camera-payment',
							label: __(
								'Monthly payment',
								'sprintray-roi-calculator'
							),
							hint: __(
								'Exact amount you pay each month',
								'sprintray-roi-calculator'
							),
							children: layerNumberInput( {
								id: 'roi-calculator-layer-camera-payment',
								label: __(
									'Monthly payment',
									'sprintray-roi-calculator'
								),
								value: cameraMonthlyPayment || null,
								suffix: currencySymbol,
								onChange: ( value ) =>
									onChangeCameraMonthlyPayment( value ?? 0 ),
							} ),
						} ) }
					</div>

					<div className="roi-calculator__layer-impact">
						<div className="roi-calculator__layer-impact-label">
							{ __(
								'Impact on equipment ROI',
								'sprintray-roi-calculator'
							) }
						</div>
						<div className="roi-calculator__layer-cell-grid roi-calculator__layer-cell-grid--two">
							{ layerResultCell(
								__(
									'Camera payment',
									'sprintray-roi-calculator'
								),
								formatCurrency(
									cameraMonthlyPayment || 0,
									currency,
									locale
								)
							) }
							{ layerResultCell(
								__(
									'Adjusted ROI',
									'sprintray-roi-calculator'
								),
								adjustedRoiMonths === null
									? __( 'N/A', 'sprintray-roi-calculator' )
									: sprintf(
											/* translators: %d: number of months */
											__(
												'%d months',
												'sprintray-roi-calculator'
											),
											adjustedRoiMonths
									  )
							) }
						</div>
						{ addedMonths > 0 && (
							<p className="roi-calculator__layer-impact-note">
								{ sprintf(
									/* translators: 1: added months, 2: base months for ROI, 3: adjusted months for ROI */
									__(
										'Adds +%1$d months to ROI (without camera: %2$d months → with: %3$d months)',
										'sprintray-roi-calculator'
									),
									addedMonths,
									monthsForRoi,
									adjustedRoiMonths
								) }
							</p>
						) }
					</div>
				</div>
			) }
		</div>
	);
};

// Reference tool's (simuprint.vercel.app) "Optional layers" panel — three
// toggleable modules valuing upside the headline ROI (ResultsStep)
// deliberately excludes. Praxis-only: the Labor calculator's own mandatory
// leasing step (LeasingStep.js) already covers the "compare to a monthly
// payment" idea for that variant.
const OptionalLayersStep = ( {
	layers,
	hasMaterialStep,
	freedSlotsPerMonth,
	chairTimeLayerEnabled,
	onToggleChairTimeLayer,
	replacementActFee,
	onChangeReplacementActFee,
	targetConversionRate,
	onChangeTargetConversionRate,
	provisionalCasesAvoided,
	onChangeProvisionalCasesAvoided,
	savingPerProvisional,
	onChangeSavingPerProvisional,
	chairTimeMonthly,
	chairTimeAnnualPotential,
	financingLayerEnabled,
	onToggleFinancingLayer,
	leaseTermMonths,
	onChangeLeaseTermMonths,
	annualInterestRate,
	onChangeAnnualInterestRate,
	calculatedMonthlyRate,
	contractualRateOverride,
	onChangeContractualRateOverride,
	effectiveMonthlyRate,
	netMonthlyCashFlow,
	cameraLayerEnabled,
	onToggleCameraLayer,
	cameraMonthlyPayment,
	onChangeCameraMonthlyPayment,
	adjustedRoiMonths,
	addedMonths,
	monthlySavings,
	monthsForRoi,
	currency,
	locale,
} ) => (
	<div className="roi-calculator__step roi-calculator__step--layers">
		<h3>{ __( 'Optional layers', 'sprintray-roi-calculator' ) }</h3>
		<p className="roi-calculator__step-hint">
			{ __(
				'Enable them if you consider them realistic for your practice — not included in the main ROI.',
				'sprintray-roi-calculator'
			) }
		</p>
		<div className="roi-calculator__layers-grid">
			{ hasMaterialStep &&
				layers.chairTime.show &&
				chairTimeFreedCard( {
					title: layers.chairTime.title,
					description: layers.chairTime.description,
					enabled: chairTimeLayerEnabled,
					onToggle: onToggleChairTimeLayer,
					freedSlotsPerMonth,
					replacementActFee,
					onChangeReplacementActFee,
					targetConversionRate,
					onChangeTargetConversionRate,
					provisionalCasesAvoided,
					onChangeProvisionalCasesAvoided,
					savingPerProvisional,
					onChangeSavingPerProvisional,
					chairTimeMonthly,
					annualPotential: chairTimeAnnualPotential,
					currency,
					locale,
				} ) }
			{ layers.financing.show &&
				financingCard( {
					title: layers.financing.title,
					description: layers.financing.description,
					enabled: financingLayerEnabled,
					onToggle: onToggleFinancingLayer,
					leaseTermMonths,
					onChangeLeaseTermMonths,
					annualInterestRate,
					onChangeAnnualInterestRate,
					calculatedMonthlyRate,
					contractualRateOverride,
					onChangeContractualRateOverride,
					monthlySavings,
					effectiveMonthlyRate,
					netMonthlyCashFlow,
					currency,
					locale,
				} ) }
			{ layers.camera.show &&
				intraoralCameraCard( {
					title: layers.camera.title,
					description: layers.camera.description,
					enabled: cameraLayerEnabled,
					onToggle: onToggleCameraLayer,
					cameraMonthlyPayment,
					onChangeCameraMonthlyPayment,
					monthsForRoi,
					adjustedRoiMonths,
					addedMonths,
					currency,
					locale,
				} ) }
		</div>
	</div>
);

const layerSettingsShape = PropTypes.shape( {
	show: PropTypes.bool.isRequired,
	title: PropTypes.string,
	description: PropTypes.string,
} );

OptionalLayersStep.propTypes = {
	layers: PropTypes.shape( {
		chairTime: layerSettingsShape.isRequired,
		financing: layerSettingsShape.isRequired,
		camera: layerSettingsShape.isRequired,
	} ).isRequired,
	hasMaterialStep: PropTypes.bool,
	freedSlotsPerMonth: PropTypes.number,
	chairTimeLayerEnabled: PropTypes.bool.isRequired,
	onToggleChairTimeLayer: PropTypes.func.isRequired,
	replacementActFee: PropTypes.number,
	onChangeReplacementActFee: PropTypes.func.isRequired,
	targetConversionRate: PropTypes.number,
	onChangeTargetConversionRate: PropTypes.func.isRequired,
	provisionalCasesAvoided: PropTypes.number,
	onChangeProvisionalCasesAvoided: PropTypes.func.isRequired,
	savingPerProvisional: PropTypes.number,
	onChangeSavingPerProvisional: PropTypes.func.isRequired,
	chairTimeMonthly: PropTypes.number,
	chairTimeAnnualPotential: PropTypes.number,
	financingLayerEnabled: PropTypes.bool.isRequired,
	onToggleFinancingLayer: PropTypes.func.isRequired,
	leaseTermMonths: PropTypes.number,
	onChangeLeaseTermMonths: PropTypes.func.isRequired,
	annualInterestRate: PropTypes.number,
	onChangeAnnualInterestRate: PropTypes.func.isRequired,
	calculatedMonthlyRate: PropTypes.number,
	contractualRateOverride: PropTypes.number,
	onChangeContractualRateOverride: PropTypes.func.isRequired,
	effectiveMonthlyRate: PropTypes.number,
	netMonthlyCashFlow: PropTypes.number,
	cameraLayerEnabled: PropTypes.bool.isRequired,
	onToggleCameraLayer: PropTypes.func.isRequired,
	cameraMonthlyPayment: PropTypes.number,
	onChangeCameraMonthlyPayment: PropTypes.func.isRequired,
	adjustedRoiMonths: PropTypes.number,
	addedMonths: PropTypes.number,
	monthlySavings: PropTypes.number,
	monthsForRoi: PropTypes.number,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default OptionalLayersStep;
